import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/comics';

const categoryNameMap: Record<string, string> = {
  romance: '恋爱',
  fantasy: '奇幻',
  action: '动作',
  drama: '剧情',
  comedy: '搞笑',
  thriller: '悬疑',
  daily: '日常',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '0', 10);
  const comic = comics.find((c) => c.id === comicId);
  const [isReading, setIsReading] = useState(false);
  const [currentChapter, setCurrentChapter] = useState<number | null>(null);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-4">您要查看的漫画不存在或已下架</p>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    {
      label: comic.categories[0]
        ? `${categoryNameMap[comic.categories[0]] || comic.categories[0]}韩漫`
        : '分类',
      href: `/category/${comic.categories[0] || 'all'}`,
    },
    { label: comic.title },
  ];

  const startReading = (chapterNum: number) => {
    setCurrentChapter(chapterNum);
    setIsReading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画全集免费在线阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-01',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.hanmanwang.com/article/${comic.id}`,
    },
  };

  if (isReading && currentChapter) {
    const chapter = comic.chapters_list?.find((ch) => ch.number === currentChapter);
    return (
      <>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-16">
            <Breadcrumb
              items={[
                ...breadcrumbItems,
                { label: chapter?.title || `第${currentChapter}话` },
              ]}
            />

            <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
              <div className="flex items-center justify-between mb-6 bg-background-100 rounded-lg p-4">
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">{comic.title}</h2>
                  <p className="text-xs text-foreground-500 mt-1">
                    {chapter?.title || `第${currentChapter}话`}
                  </p>
                </div>
                <button
                  onClick={() => setIsReading(false)}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回详情
                </button>
              </div>

              {/* Reading Content - Dropdown style manga reader */}
              <div className="flex flex-col gap-2">
                {Array.from({ length: 12 }, (_, i) => (
                  <div
                    key={i}
                    className="w-full aspect-[3/2] bg-background-100 rounded-lg flex items-center justify-center border border-background-200/50"
                  >
                    <div className="text-center text-foreground-400">
                      <i className="ri-image-line text-4xl block mb-2"></i>
                      <span className="text-sm">漫画内容第 {i + 1} 页</span>
                      <p className="text-xs mt-1 text-foreground-300">
                        （下拉式阅读模式 - 滚动查看完整内容）
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Chapter Navigation */}
              <div className="flex items-center justify-between mt-8 mb-4 bg-background-100 rounded-lg p-4">
                <button
                  disabled={currentChapter <= 1}
                  onClick={() => {
                    const prev = comic.chapters_list?.find((ch) => ch.number === currentChapter - 1);
                    if (prev) startReading(prev.number);
                  }}
                  className={`text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                    currentChapter <= 1
                      ? 'text-foreground-300 cursor-not-allowed'
                      : 'text-primary-600 hover:text-primary-700'
                  }`}
                >
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </button>
                <span className="text-xs text-foreground-500">
                  {currentChapter} / {comic.chapters}
                </span>
                <button
                  disabled={currentChapter >= comic.chapters}
                  onClick={() => {
                    const next = comic.chapters_list?.find((ch) => ch.number === currentChapter + 1);
                    if (next) startReading(next.number);
                  }}
                  className={`text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                    currentChapter >= comic.chapters
                      ? 'text-foreground-300 cursor-not-allowed'
                      : 'text-primary-600 hover:text-primary-700'
                  }`}
                >
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <main>
            {/* Comic Detail Header */}
            <section className="bg-background-100 py-8 md:py-10">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                  <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full aspect-[3/4] object-cover rounded-lg"
                      title={`${comic.title} - 韩国漫画全集免费在线阅读`}
                    />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-900">
                      {comic.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                      <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-600' : 'text-secondary-600'}`}>
                        {comic.status}
                      </span>
                      <span className="text-sm text-foreground-400">共 {comic.chapters} 话</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      {comic.categories.map((cat) => (
                        <Link
                          key={cat}
                          to={`/category/${cat}`}
                          className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded whitespace-nowrap hover:bg-secondary-200 transition-colors"
                        >
                          {categoryNameMap[cat] || cat}
                        </Link>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        <strong className="text-foreground-900">{comic.rating}</strong> 分
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {formatViews(comic.views)} 次阅读
                      </span>
                      <span>更新于 {comic.updateTime}</span>
                    </div>

                    <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-2xl">
                      {comic.description}
                    </p>

                    <div className="flex items-center gap-3 mt-6 flex-wrap">
                      {comic.chapters_list && comic.chapters_list.length > 0 && (
                        <button
                          onClick={() => startReading(comic.chapters_list![0].number)}
                          className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-play-circle-line"></i>
                          开始阅读（下拉式）
                        </button>
                      )}
                      <button
                        onClick={() => {
                          const latest = comic.chapters_list?.[comic.chapters_list.length - 1];
                          if (latest) startReading(latest.number);
                        }}
                        className="border border-primary-300 text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-skip-forward-line"></i>
                        阅读最新章节
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Chapter List */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">
                章节列表（下拉式在线阅读）
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {comic.chapters_list?.map((chapter) => (
                  <button
                    key={chapter.id}
                    onClick={() => startReading(chapter.number)}
                    className="flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer text-left"
                  >
                    <span className="text-sm text-foreground-700 truncate">{chapter.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.updateTime}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Related Comics */}
            <section className="bg-background-100 py-10 md:py-14">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {comics
                    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
                    .slice(0, 4)
                    .map((c) => (
                      <Link
                        key={c.id}
                        to={`/article/${c.id}`}
                        className="group flex gap-3 bg-background-50 rounded-lg p-3 border border-background-200/70 hover:border-primary-200 transition-colors"
                      >
                        <img
                          src={c.cover}
                          alt={c.title}
                          className="w-14 h-20 object-cover rounded flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                            {c.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-1">{c.author}</p>
                          <p className="text-xs text-foreground-400 mt-1">
                            {c.status} · {c.chapters}话
                          </p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}